import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { _ as _object_without_properties } from "@swc/helpers/_/_object_without_properties";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useCallback, useState } from 'react';
import { KeyboardDatePicker as MuiKeyboardDatePicker, KeyboardDateTimePicker as MuiKeyboardDateTimePicker } from '@material-ui/pickers';
import cn from 'classnames';
import { isSameDay } from 'date-fns';
import { ColorCalendar } from '../../icons';
import { useDevice } from '../../providers/Device';
import { useIsInBottomSheet } from '../../providers/IsInBottomSheetProvider';
import { useBiAction } from '../BiTracking';
import { Spinner } from '../Spinner';
import styles from './KeyboardDatePicker.module.scss';
import { useKeyboardDatePickerStyles } from './KeyboardDatePicker.styles';
const defaultDayHighlightSkin = 'standard';
// WHY: mirrors $portalledCalendar in foundations/zIndex.module.scss — jest maps .scss to
// identity-obj-proxy, so reading the token through the module yields NaN at test time.
const PORTALLED_CALENDAR_Z_INDEX = 1500;
const CALENDAR_OVERLAY_STYLE = {
    style: {
        zIndex: PORTALLED_CALENDAR_Z_INDEX
    }
};
const DEFAULT_DATE_FORMAT = 'PP';
const DEFAULT_DATETIME_FORMAT = 'MMM d, yyyy hh:mm a';
export function KeyboardDatePicker(props) {
    // IMPORTANT: Extract highlightDay BEFORE destructuring to avoid it being spread into rest
    const highlightDay = 'highlightDay' in props ? props.highlightDay : undefined;
    const { loading, disabled, disableErrors, size = 'medium', autoOk = true, inputVariant = 'outlined', variant: variantProp, onChange, helperTextReserveSpace, withTime = false, format: formatProp, highlightDay: _, testId, bi, noBi: _noBi } = props, rest = _object_without_properties(props, [
        "loading",
        "disabled",
        "disableErrors",
        "size",
        "autoOk",
        "inputVariant",
        "variant",
        "onChange",
        "helperTextReserveSpace",
        "withTime",
        "format",
        "highlightDay",
        "testId",
        "bi",
        "noBi"
    ]);
    // IMPORTANT: Compute format AFTER destructuring so withTime is available
    const format = formatProp !== null && formatProp !== void 0 ? formatProp : withTime ? DEFAULT_DATETIME_FORMAT : DEFAULT_DATE_FORMAT;
    const { isMobileDevice } = useDevice();
    const isInBottomSheet = useIsInBottomSheet();
    // WHY: MUI's 'inline' variant anchors the calendar in a popover, which a touch device's hosting
    // bottom sheet dismisses before a day can be tapped; 'dialog' is MUI's touch-intended mode.
    const variant = variantProp !== null && variantProp !== void 0 ? variantProp : isMobileDevice || isInBottomSheet ? 'dialog' : 'inline';
    // WHY: the dialog calendar portals to <body>, so it must out-stack the overlay that opened it.
    // The inline popover anchors to the field and needs no override.
    const calendarOverlayProps = variant === 'dialog' ? {
        DialogProps: CALENDAR_OVERLAY_STYLE
    } : {};
    const isDisabled = disabled || loading;
    const overrides = useKeyboardDatePickerStyles({
        isDisabled
    });
    const [open, setOpen] = useState(false);
    const logBiAction = useBiAction();
    const fireBi = useCallback((dateValue)=>{
        if (bi === null || bi === void 0 ? void 0 : bi.name) {
            logBiAction({
                entityType: 'date_picker',
                entityName: bi.name,
                actionType: 'select',
                data: _object_spread_props(_object_spread({}, bi.data), {
                    test_id: testId !== null && testId !== void 0 ? testId : '',
                    selected_value: dateValue
                })
            });
        }
    }, [
        bi,
        logBiAction,
        testId
    ]);
    const handleChange = useCallback((date, value)=>{
        const isValid = date instanceof Date && !isNaN(date.getTime());
        if (isValid) fireBi(date.toISOString().split('T')[0]);
        onChange(isValid ? new Date(date.setHours(0, 0, 0, 0)) : null, value);
    }, [
        fireBi,
        onChange
    ]);
    const handleChangeWithTime = useCallback((date, value)=>{
        const isValid = date instanceof Date && !isNaN(date.getTime());
        if (isValid) fireBi(date.toISOString());
        onChange(date, value);
    }, [
        fireBi,
        onChange
    ]);
    const disableErrorsProps = disableErrors || open ? {
        error: false,
        helperText: null
    } : {};
    const handleOpen = ()=>{
        setOpen(true);
    };
    const handleClose = ()=>{
        setOpen(false);
    };
    // Date-time mode: use MuiKeyboardDateTimePicker, no normalization
    if (withTime) {
        return /*#__PURE__*/ _jsx(MuiKeyboardDateTimePicker, _object_spread_props(_object_spread(_object_spread_props(_object_spread({}, rest, disableErrorsProps), {
            "data-testid": testId,
            disabled: isDisabled,
            keyboardIcon: loading ? /*#__PURE__*/ _jsx(Spinner, {
                size: 24,
                testId: testId && "".concat(testId, "-loading")
            }) : /*#__PURE__*/ _jsx(ColorCalendar, {
                style: isDisabled ? {
                    opacity: 0.4
                } : undefined
            }),
            autoOk: autoOk,
            format: format,
            inputVariant: inputVariant,
            variant: variant
        }), calendarOverlayProps), {
            focused: open,
            size: size,
            inputProps: _object_spread({
                'data-testid': testId && "".concat(testId, "-input")
            }, props.inputProps),
            open: open,
            onOpen: handleOpen,
            onClose: handleClose,
            KeyboardButtonProps: _object_spread({
                'data-testid': testId && "".concat(testId, "-btn")
            }, props.KeyboardButtonProps),
            leftArrowButtonProps: _object_spread({
                'data-testid': testId && "".concat(testId, "-prev-month")
            }, props.leftArrowButtonProps),
            rightArrowButtonProps: _object_spread({
                'data-testid': testId && "".concat(testId, "-next-month")
            }, props.rightArrowButtonProps),
            FormHelperTextProps: _object_spread({
                ['data-testid']: testId && "".concat(testId, "-helper-text"),
                classes: {
                    contained: cn(overrides.helperText, {
                        positionFixed: !helperTextReserveSpace
                    })
                }
            }, props.FormHelperTextProps),
            InputLabelProps: {
                classes: {
                    root: overrides.label
                }
            },
            InputProps: _object_spread({
                disabled: isDisabled,
                readOnly: true,
                classes: {
                    root: overrides.input,
                    adornedEnd: overrides.adornedEnd,
                    input: cn(overrides.inputInput, overrides["inputInput_".concat(size)])
                }
            }, props.InputProps),
            onChange: handleChangeWithTime
        }));
    }
    // Date-only mode: use MuiKeyboardDatePicker with normalization and highlightDay
    return /*#__PURE__*/ _jsx(MuiKeyboardDatePicker, _object_spread_props(_object_spread(_object_spread_props(_object_spread({}, rest, disableErrorsProps), {
        "data-testid": testId,
        disabled: isDisabled,
        keyboardIcon: loading ? /*#__PURE__*/ _jsx(Spinner, {
            size: 24,
            testId: testId && "".concat(testId, "-loading")
        }) : /*#__PURE__*/ _jsx(ColorCalendar, {
            style: isDisabled ? {
                opacity: 0.4
            } : undefined
        }),
        autoOk: autoOk,
        format: format,
        inputVariant: inputVariant,
        variant: variant
    }), calendarOverlayProps), {
        focused: open,
        size: size,
        inputProps: _object_spread({
            'data-testid': testId && "".concat(testId, "-input")
        }, props.inputProps),
        open: open,
        onOpen: handleOpen,
        onClose: handleClose,
        KeyboardButtonProps: _object_spread({
            'data-testid': testId && "".concat(testId, "-btn")
        }, props.KeyboardButtonProps),
        leftArrowButtonProps: _object_spread({
            'data-testid': testId && "".concat(testId, "-prev-month")
        }, props.leftArrowButtonProps),
        rightArrowButtonProps: _object_spread({
            'data-testid': testId && "".concat(testId, "-next-month")
        }, props.rightArrowButtonProps),
        FormHelperTextProps: _object_spread({
            ['data-testid']: testId && "".concat(testId, "-helper-text"),
            classes: {
                contained: cn(overrides.helperText, {
                    positionFixed: !helperTextReserveSpace
                })
            }
        }, props.FormHelperTextProps),
        InputLabelProps: {
            classes: {
                root: overrides.label
            }
        },
        InputProps: _object_spread({
            disabled: isDisabled,
            readOnly: true,
            classes: {
                root: overrides.input,
                adornedEnd: overrides.adornedEnd,
                input: cn(overrides.inputInput, overrides["inputInput_".concat(size)])
            }
        }, props.InputProps),
        renderDay: (day, selectedDate, dayInCurrentMonth, dayComponent)=>{
            const highlighted = day && dayInCurrentMonth && highlightDay ? highlightDay(day) : false;
            const dayTestId = day && testId ? "".concat(testId, "-day-").concat(day.getMonth() + 1, "-").concat(day.getDate()) : undefined;
            return /*#__PURE__*/ _jsxs("div", {
                className: styles.dayWrapper,
                "data-testid": dayTestId,
                children: [
                    !!highlighted && !(day && selectedDate && isSameDay(day, selectedDate)) && /*#__PURE__*/ _jsx("div", {
                        className: cn(styles.dayHighlight, styles[typeof highlighted === 'string' ? highlighted : defaultDayHighlightSkin]),
                        "data-testid": dayTestId && "".concat(dayTestId, "-highlighted")
                    }),
                    dayComponent
                ]
            });
        },
        onChange: handleChange
    }));
}
